import { v4 as uuidv4 } from "uuid";
import "leader-line";
import {MuleMapping} from "./mule-mapping";
import {MuleService} from "./mule-service";

declare var LeaderLine: any;

export class MappingBoard {
    public id: string;
    public boardIndex: number;
    public isLtr: boolean;
    public type: string;
    public showBoard: any = { value: true };
    public showRightSideFields: boolean;
    public isQueryWriteService: boolean;
    public muleServiceItems: MuleMapping[];
    public prefix: string;

    // lines
    public lines: any[] = [];
    public ghostLines: any[] = [];
    // leader line vars
    public LeaderLineColor: string = "#2AA3EE";
    public LeaderLineWarningColor: string = "#FFAF89";
    public leaderLineOptions: any = {
        startSocketGravity: 60,
        endSocketGravity: 60,
        path: "fluid",
        startPlug: "behind",
        endPlug: "behind",
        endPlugSize: 1,
        size: 2,
        color: this.LeaderLineColor,
    };

    // drag vars
    public dragSrc: any = { el: null };
    public isDragging: any = { value: false };

    public visible: boolean = true;

    public onlyVisibleLeftField: boolean = true;
    public onlyVisibleRightField: boolean = true;

    public collapseFieldsLeft: boolean = true;
    public collapseFieldsRight: boolean = true;

    public leftFilter: any;
    public rightFilter: any;

    public leftIdPrefix: string;
    public rightIdPrefix: string;

    public dragulaNameLeft: string;
    public dragulaNameRight: string;
    public selectedMuleService: MuleService;

    constructor(selectedMuleService: MuleService, isQueryWriteService: boolean = false, prefix: string = "", boardIndex: number = 0, isLtr: boolean = true, type: string = "input") {

        this.id = uuidv4();
        this.isLtr = isLtr;
        this.type = type;
        this.selectedMuleService = selectedMuleService;
        this.isQueryWriteService = isQueryWriteService;
        this.prefix = prefix;
        this.boardIndex = boardIndex;

        this.leftIdPrefix = uuidv4() + "-";
        this.rightIdPrefix = uuidv4() + "-";
        this.dragulaNameLeft = uuidv4();
        this.dragulaNameRight = uuidv4();

        this.showRightSideFields = isLtr
            ? (selectedMuleService && selectedMuleService.inputs && selectedMuleService.inputs.length > 0)
            : (selectedMuleService && selectedMuleService.outputs && selectedMuleService.outputs.length > 0);

        if (!selectedMuleService) {
            this.muleServiceItems = [];
        } else if (isQueryWriteService) {
            this.muleServiceItems = selectedMuleService.inputs.filter((input) => input.id.startsWith(prefix));
        } else {
            this.muleServiceItems = isLtr ? selectedMuleService.inputs : selectedMuleService.outputs;
        }
    }

    public removeSelection(): void {
        this.dragSrc.el = null;
        this.isDragging.value = false;
    }

    public removeRelationship(id): void {
        const linesToRemove = [];
        this.lines.forEach((line, i) => {
            console.log(id, line.lineId, id !== line.lineId);
            if (id !== line.lineId) { return; }
            if (line.line !== null) {
                line.line.remove();
                line.line = null;
            }
            linesToRemove.push(i);
            return true;
        });
        linesToRemove.sort((a, b) => b - a).forEach((ltr) => {
            this.lines.splice(ltr, 1);
        });
    }

    public initLine(start, end, options): any {
        this.leaderLineOptions.gradient = MappingBoard.validateTypeCompatibility(options.field ? options.field.remoteType : null, options.input.type, this.LeaderLineColor, this.LeaderLineWarningColor);
        if (this.compareStringOccurrences(options.start, options.end, options.input.type)) {
            this.leaderLineOptions.gradient = { startColor: this.LeaderLineWarningColor, endColor: this.LeaderLineWarningColor };
        }
        if (options.type === "input") {
            return new LeaderLine(start, end, this.leaderLineOptions);
        }
        return new LeaderLine(end, start, this.leaderLineOptions);
    }

    public compareStringOccurrences(start, end, endType) {
        const regex = /(_child_\*_child_|_collection_)/g;
        const startOccurrences = (String(start).match(regex) || []).length;
        const endOccurrences = (String(end).match(regex) || []).length;
        // console.log(start, startOccurrences, end, endOccurrences, endType);
        if (endType === "file" && start.split("_").pop() === "item" && end.split("_")[end.split("_").length - 2] === "child") {
            return true;
        }
        if (startOccurrences >= 0 && endOccurrences >= 0) {
            return startOccurrences !== endOccurrences;
        } else {
            return false;
        }
    }

    public addGhost(args): void {
        const options = Object(args);
        const line = {
            start: options.startPoint,
            end: options.endPoint,
            type: options.type,
            field: options.field,
            input: options.input,
            line: null,
            lineId: options.lineId,
            isDeletedSource: options.isDeletedSource || false,
            targetFieldProperties: options.targetFieldProperties,
            jsonDeterminant: options.jsonDeterminant,
            jsonDeterminantFilter: options.jsonDeterminantFilter,
        };
        this.ghostLines.push(line);
        console.log("ghostLines", this.ghostLines);
    }

    public addRelationship(args): void {
        const options = Object(args);
        const line = {
            start: options.startPoint,
            end: options.endPoint,
            type: options.type,
            field: options.field,
            input: options.input,
            line: null,
            lineId: options.lineId,
            targetFieldProperties: options.targetFieldProperties,
            jsonDeterminant: options.jsonDeterminant,
            jsonDeterminantFilter: options.jsonDeterminantFilter,
        };
        if (!this.validateOrigins(options.startPoint, options.endPoint)) { return; }
        this.lines.push(line);
    }

    public static validateTypeCompatibility(srcType, remoteType, color, warningColor) {
        const lineOk = { startColor: color, endColor: color };
        const lineOkeish = { startColor: color, endColor: warningColor };
        const lineNotOk = { startColor: warningColor, endColor: warningColor };
        if (srcType === null || srcType === undefined) { return null; }
        if (remoteType === null || remoteType === undefined) { return null; }
        if (srcType.toLocaleLowerCase() === "string") {
            switch (remoteType.toLowerCase()) {
                case "selectboxes":
                case "classification":
                case "text":
                case "string":
                case "textfield":
                case "mycontent":
                case "email":
                case "textarea":
                case "key":
                case "select":
                case "select_folder":
                    return lineOk;
                case "numeric":
                case "number":
                case "decimal":
                case "integer":
                case "date":
                case "datetime":
                case "date-time":
                case "boolean":
                case "checkbox":
                case "radio":
                    return lineOkeish;
                case "catalog":
                case "document":
                case "file":
                case "requirement":
                case "result":
                case "fileupload":
                default:
                    return lineNotOk;
            }
        }
        if (srcType.toLocaleLowerCase() === "number" || srcType.toLocaleLowerCase() === "integer") {
            switch (remoteType.toLowerCase()) {
                case "numeric":
                case "number":
                case "decimal":
                case "integer":
                    return lineOk;
                case "selectboxes":
                case "classification":
                case "text":
                case "string":
                case "textfield":
                case "mycontent":
                case "radio":
                case "textarea":
                case "key":
                case "select":
                    return lineOkeish;
                case "date":
                case "datetime":
                case "date-time":
                case "boolean":
                case "catalog":
                case "checkbox":
                case "document":
                case "fileupload":
                case "file":
                case "requirement":
                case "result":
                case "email":
                default:
                    return lineNotOk;
            }
        }
        if (srcType.toLocaleLowerCase() === "date") {
            switch (remoteType.toLowerCase()) {
                case "date":
                case "datetime":
                case "date-time":
                    return lineOk;
                case "selectboxes":
                case "classification":
                case "text":
                case "string":
                case "textfield":
                case "mycontent":
                case "radio":
                case "textarea":
                    return lineOkeish;
                case "email":
                case "numeric":
                case "number":
                case "decimal":
                case "integer":
                case "boolean":
                case "catalog":
                case "checkbox":
                case "document":
                case "file":
                case "requirement":
                case "result":
                case "fileupload":
                case "key":
                case "select":
                default:
                    return lineNotOk;
            }
        }
        if (srcType.toLocaleLowerCase() === "boolean") {
            switch (remoteType.toLowerCase()) {
                case "boolean":
                case "checkbox":
                case "radio":
                    return lineOk;
                case "selectboxes":
                case "classification":
                case "text":
                case "string":
                case "textfield":
                case "mycontent":
                case "textarea":
                    return lineOkeish;
                case "email":
                case "numeric":
                case "number":
                case "decimal":
                case "integer":
                case "date":
                case "datetime":
                case "date-time":
                case "catalog":
                case "document":
                case "file":
                case "requirement":
                case "result":
                case "fileupload":
                case "key":
                case "select":
                default:
                    return lineNotOk;
            }
        }
        if (srcType.toLocaleLowerCase() === "radio") {
            switch (remoteType.toLowerCase()) {
                case "checkbox":
                case "selectboxes":
                case "classification":
                case "text":
                case "string":
                case "textfield":
                case "mycontent":
                case "textarea":
                case "radio":
                    return lineOk;
                case "boolean":
                  return lineOkeish;
                case "email":
                case "numeric":
                case "number":
                case "decimal":
                case "integer":
                case "date":
                case "datetime":
                case "date-time":
                case "catalog":
                case "document":
                case "file":
                case "requirement":
                case "result":
                case "fileupload":
                case "key":
                case "select":
                default:
                    return lineNotOk;
            }
        }
        if (srcType.toLocaleLowerCase() === "catalog" || srcType.toLocaleLowerCase() === "select_folder" || srcType.toLocaleLowerCase() === "select") {
            switch (remoteType.toLowerCase()) {
                case "string":
                case "select_folder":
                case "select":
                case "catalog":
                    return lineOk;
                case "selectboxes":
                case "classification":
                case "text":
                case "textfield":
                case "mycontent":
                case "numeric":
                case "number":
                case "decimal":
                case "integer":
                case "date":
                case "datetime":
                case "date-time":
                case "checkbox":
                case "document":
                case "file":
                case "requirement":
                case "result":
                case "fileupload":
                case "textarea":
                case "email":
                case "key":
                default:
                    return lineNotOk;
            }
        }
        if (srcType.toLocaleLowerCase() === "file") {
            switch (remoteType.toLowerCase()) {
                case "document":
                case "file":
                case "requirement":
                case "result":
                case "fileupload":
                    return lineOk;
                case "selectboxes":
                case "classification":
                case "text":
                case "string":
                case "textfield":
                case "mycontent":
                case "numeric":
                case "number":
                case "decimal":
                case "integer":
                case "date":
                case "datetime":
                case "date-time":
                case "catalog":
                case "checkbox":
                case "textarea":
                case "email":
                case "key":
                case "select":
                default:
                    return lineNotOk;
            }
        }

        if (srcType.toLocaleLowerCase() === "editgrid" || srcType.toLocaleLowerCase() === "datagrid") {
            switch (remoteType.toLowerCase()) {
                case "datagrid":
                case "editgrid":
                    return lineOk;
                case "file":
                case "requirement":
                case "result":
                case "fileupload":
                case "selectboxes":
                case "classification":
                case "text":
                case "string":
                case "textfield":
                case "mycontent":
                case "numeric":
                case "number":
                case "decimal":
                case "integer":
                case "date":
                case "datetime":
                case "date-time":
                case "catalog":
                case "checkbox":
                case "textarea":
                case "email":
                case "key":
                case "select":
                default:
                    return lineNotOk;
            }
        }

        if (srcType.toLowerCase() === remoteType.toLowerCase()) {
            return lineOk;
        }
        return lineNotOk;
    }

    private validateOrigins(startPoint, endPoint): boolean {
        return this.lines.every((line) => {
            return !(line.start === startPoint && line.end === endPoint);
        });
    }
}
